import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ---- Font sizes ---- */
const FONT_SIZES = [
  { label: 'Default', value: null },
  { label: '7pt', value: '7pt' },
  { label: '8pt', value: '8pt' },
  { label: '9pt', value: '9pt' },
  { label: '10pt', value: '10pt' },
  { label: '11pt', value: '11pt' },
  { label: '12pt', value: '12pt' },
  { label: '14pt', value: '14pt' },
  { label: '16pt', value: '16pt' },
  { label: '18pt', value: '18pt' },
];

/* ---- Preset colours ---- */
const COLOURS = [
  { label: 'Default', value: null },
  { label: 'Navy', value: '#072040' },
  { label: 'Gold', value: '#f0a500' },
  { label: 'Blue', value: '#599BE3' },
  { label: 'Dark', value: '#212529' },
  { label: 'Grey', value: '#6c757d' },
  { label: 'Green', value: '#91CF9B' },
  { label: 'Red', value: '#dc3545' },
];

/* ---- Dropdown ---- */
const ToolbarDropdown = ({ label, options, currentValue, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const activeOption = options.find(o => o.value === currentValue);

  return (
    <div className="tiptap-dropdown" ref={ref}>
      <button
        type="button"
        className="tiptap-btn tiptap-dropdown-trigger"
        onMouseDown={(e) => { e.preventDefault(); setOpen(!open); }}
        title={label}
      >
        <span className="tiptap-dropdown-label">{activeOption?.label || label}</span>
        <span className="tiptap-dropdown-arrow">▾</span>
      </button>
      {open && (
        <div className="tiptap-dropdown-menu">
          {options.map((opt) => (
            <button
              key={opt.label}
              type="button"
              className={`tiptap-dropdown-item ${opt.value === currentValue ? 'is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onSelect(opt.value); setOpen(false); }}
            >
              {opt.swatch && (
                <span className="tiptap-colour-swatch" style={{ background: opt.swatch }} />
              )}
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Floating toolbar that appears above the currently focused text block
 * on the A4 preview. Re-uses the same TipTap toolbar buttons as
 * RichTextEditor's MenuBar.
 */
const FloatingToolbar = ({ editor, anchorEl }) => {
  const toolbarRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, visible: false });

  // Reposition whenever editor or anchor changes
  const reposition = useCallback(() => {
    if (!editor || !anchorEl || !toolbarRef.current) {
      setPos(p => ({ ...p, visible: false }));
      return;
    }

    const anchorRect = anchorEl.getBoundingClientRect();
    const toolbarRect = toolbarRef.current.getBoundingClientRect();
    const scrollContainer = anchorEl.closest('.fact-sheet-content');
    const containerRect = scrollContainer
      ? scrollContainer.getBoundingClientRect()
      : { top: 0, left: 0, right: window.innerWidth };

    // Place above the block by default
    let top = anchorRect.top - toolbarRect.height - 6;
    // If that clips above the container, place below instead
    if (top < containerRect.top) {
      top = anchorRect.bottom + 6;
    }

    // Centre horizontally on the block, clamped to viewport
    let left = anchorRect.left + anchorRect.width / 2 - toolbarRect.width / 2;
    left = Math.max(4, Math.min(left, window.innerWidth - toolbarRect.width - 4));

    setPos({ top, left, visible: true });
  }, [editor, anchorEl]);

  useEffect(() => {
    reposition();
    // Re-position on scroll/resize
    const scrollContainer = anchorEl?.closest('.fact-sheet-content');
    const handler = () => reposition();
    window.addEventListener('resize', handler);
    if (scrollContainer) scrollContainer.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('resize', handler);
      if (scrollContainer) scrollContainer.removeEventListener('scroll', handler);
    };
  }, [reposition]);

  if (!editor) return null;

  const currentColour = editor.getAttributes('textStyle').color || null;
  const currentSize = editor.getAttributes('textStyle').fontSize || null;
  const colourOptions = COLOURS.map(c => ({ ...c, swatch: c.value || '#212529' }));

  return (
    <div
      ref={toolbarRef}
      className="floating-toolbar"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        opacity: pos.visible ? 1 : 0,
        pointerEvents: pos.visible ? 'auto' : 'none',
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Bold / Italic */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
        title="Bold"
      >B</button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
        title="Italic"
      ><em>I</em></button>

      <span className="tiptap-separator" />

      {/* Headings */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run(); }}
        title="Heading 1"
      >H1</button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
        title="Heading 2"
      >H2</button>

      <span className="tiptap-separator" />

      {/* Font size */}
      <ToolbarDropdown
        label="Size"
        options={FONT_SIZES}
        currentValue={currentSize}
        onSelect={(size) => {
          if (size) {
            editor.chain().focus().setFontSize(size).run();
          } else {
            editor.chain().focus().unsetFontSize().run();
          }
        }}
      />

      <span className="tiptap-separator" />

      {/* Lists */}
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
        title="Bullet list"
      >&bull;</button>
      <button
        type="button"
        className={`tiptap-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
        title="Numbered list"
      >1.</button>

      <span className="tiptap-separator" />

      {/* Colour */}
      <ToolbarDropdown
        label="Colour"
        options={colourOptions}
        currentValue={currentColour}
        onSelect={(colour) => {
          if (colour) {
            editor.chain().focus().setColor(colour).run();
          } else {
            editor.chain().focus().unsetColor().run();
          }
        }}
      />

      <span className="tiptap-separator" />

      {/* Clear formatting */}
      <button
        type="button"
        className="tiptap-btn"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetAllMarks().clearNodes().run(); }}
        title="Clear formatting"
      >✕</button>
    </div>
  );
};

export default FloatingToolbar;
